export const REGISTER_URL = process.env.MIX_APP_MYDEV_REGISTER_REDIRECT_URL;
export const SIGNIN_URL = process.env.MIX_APP_MYDEV_REDIRECT_URL;
export const RECAPTCHA_KEY = process.env.MIX_APP_RECAPTCHA_V3_SITE_KEY;

export const CONTACT_EMAIL = "info@claritick.com";
export const CONTACT_PHONE = "(888) 744-7388";
export const CONTACT_PHONE_HREF = "tel:+18887447388";
export const CONTACT_ADDRESS = ["6605 Warren Parkway Suite 760", "Frisco, TX 75034"];

export const CONTACT_DEMO_POINTS = [
    "Live product walkthrough, not a pre-recorded pitch",
    "Pricing and integrations discussed upfront",
    "30 minutes, no commitment required",
];

export const SOCIAL_LINKS = [
    { label: "Facebook", icon: "facebook", href: "https://www.facebook.com/Claritick-105524795322767" },
    { label: "Twitter", icon: "twitter", href: "https://www.twitter.com/claritick" },
    { label: "LinkedIn", icon: "linkedin", href: "https://www.linkedin.com/company/claritick" },
    { label: "YouTube", icon: "youtube", href: "https://www.youtube.com/channel/UCHuoIDHYD5KBNopxUFsZT2w" },
];

export const NAV_LINKS = [
    { label: "Features", path: "/", hash: "#features" },
    { label: "How it works", path: "/how-it-works" },
    { label: "Claritask", path: "/claritask" },
    { label: "Pricing", path: "/pricing" },
    { label: "Contact", path: "/contact" },
];

/* ---------- Hero ---------- */
export const TRUST_CHIPS = [
    { label: "Free to start", detail: "No credit card required" },
    { label: "One workspace", detail: "Email, forms & replies together" },
    { label: "Real-time", detail: "Live updates across your team" },
];

export const HERO_FLOAT_CHIPS = [
    { icon: "mail", bg: "bg-cyan", title: "New email → ticket", sub: "yourcompany@claritick.com" },
    { icon: "widget", bg: "bg-blue", title: "Widget submission", sub: "Contact form" },
    { icon: "ticket", bg: "bg-violet", title: "Ticket assigned", sub: "#1042 · Refund request" },
];

const WORKFLOW_IMAGE_BASE = "/images/landing/workflow/webp";

/** Product screenshots (optimized WebP) for features & marketing sections */
export const LANDING_IMAGES = {
    embedFormExact: `${WORKFLOW_IMAGE_BASE}/embed_form_exact.webp`,
    embedFormScript: `${WORKFLOW_IMAGE_BASE}/embed_form_script.webp`,
    emailTicketsSummary: `${WORKFLOW_IMAGE_BASE}/email_tickets_summary.webp`,
    ticketsSummaryEmailView: `${WORKFLOW_IMAGE_BASE}/tickets_summary_email_view.webp`,
    ticketScreenshot: `${WORKFLOW_IMAGE_BASE}/claritick_screenshot.webp`,
    ticketEmail: `${WORKFLOW_IMAGE_BASE}/email_screenshot.webp`,
    editDepartment: `${WORKFLOW_IMAGE_BASE}/claritick_edit_department.webp`,
    ticketsQueue: `${WORKFLOW_IMAGE_BASE}/claritick_screenshot_embedded.webp`,
    receiveRequest: `${WORKFLOW_IMAGE_BASE}/claritick_received_request.webp`,
    replyEmbedded: `${WORKFLOW_IMAGE_BASE}/converted_image_s.webp`,
    liveTracker: `${WORKFLOW_IMAGE_BASE}/live_tracker_embedded.webp`,
    incomingEmails: `${WORKFLOW_IMAGE_BASE}/claritick_incoming_emails.webp`,
    newTicketEmail: `${WORKFLOW_IMAGE_BASE}/claritick_new_ticket_email.webp`,
    claritaskIntegration: `${WORKFLOW_IMAGE_BASE}/claritask_integration.webp`,
    taskCreation: `${WORKFLOW_IMAGE_BASE}/claritick_task_creation_hd.webp`,
};

/** URLs to warm before /how-it-works and /claritask navigation */
export const HIW_PREFETCH_IMAGES = [
    LANDING_IMAGES.incomingEmails,
    LANDING_IMAGES.ticketsQueue,
    LANDING_IMAGES.receiveRequest,
    LANDING_IMAGES.newTicketEmail,
    LANDING_IMAGES.editDepartment,
    LANDING_IMAGES.replyEmbedded,
    LANDING_IMAGES.liveTracker,
    LANDING_IMAGES.claritaskIntegration,
    LANDING_IMAGES.taskCreation,
];

export const CLARITASK_PREFETCH_IMAGES = [
    "/images/platforms/claritick.svg",
    "/images/claritasklogo.svg",
    "/images/logo.svg",
    "/images/landing/workflow/claritask-logo-2021.svg",
    "/images/small-logo-c.svg",
    LANDING_IMAGES.claritaskIntegration,
    LANDING_IMAGES.taskCreation,
];

/* ---------- Workflow (core ticket path + Claritask) ---------- */
export const WORKFLOW_CLARITASK_EXTRA = {
    eyebrow: "Claritask integration",
    title: "Connect support and delivery",
    summary:
        "Link Claritick and Claritask in minutes. Support keeps the customer conversation; delivery executes the work behind the ticket without copy-paste or lost context.",
};

export const WORKFLOW_CORE_STEPS = [
    {
        n: "01",
        icon: "inbox",
        bg: "bg-cyan",
        accent: "cyan",
        t: "The request lands instantly",
        d: "From email, widget, in-app, or the API, customers get instant confirmation that their request is in your queue.",
        image: LANDING_IMAGES.receiveRequest,
        imageAlt: "Confirmation email sent to the customer after their support request is received",
        detail:
            "The moment a request comes in, Claritick creates a ticket and sends the customer a confirmation right away, no copy-paste, no manual entry. Every reply stays on the same thread, and they know you received it before your team even opens the app. Widget submissions, in-app tickets, and API calls follow the same path.",
        points: [
            "Instant confirmation with ticket reference and tracking link",
            "Email your mapped support address and a ticket is created automatically",
            "Follow-up replies thread to the same ticket automatically",
            "Widget, in-app, and API intake land on the same board",
        ],
    },
    {
        n: "02",
        icon: "users",
        bg: "bg-blue",
        accent: "blue",
        t: "Routed to the right team",
        d: "Department rules, inbox mapping, and auto-assign put each ticket with the right agent with no manual triage.",
        image: LANDING_IMAGES.ticketsQueue,
        imageAlt: "Support tickets listed in the Claritick queue, sorted by department and status",
        detail:
            "Incoming tickets are sorted before anyone opens them. Each inbox maps to a department, embed forms auto-assign to the right team, and agents can filter the queue by status, priority, or assignee, so the right person picks up the right request without a morning triage ritual.",
        points: [
            "Map each support inbox to a department",
            "Embed submissions auto-assign to the configured team",
            "Filter by department, priority, status, or assignee",
            "Unassigned tickets stay visible so nothing gets missed",
        ],
    },
    {
        n: "03",
        icon: "chat",
        bg: "bg-violet",
        accent: "violet",
        t: "Your team collaborates",
        d: "Reply, add internal notes, start timers, and merge duplicates in one threaded conversation.",
        image: LANDING_IMAGES.replyEmbedded,
        imageAlt: "Agent composing a reply inside a ticket detail view",
        detail:
            "Open the ticket and work it as a team. Reply to the customer, add internal notes your agents see but customers don't, track time on the issue, merge duplicates, forward to another department, and review a full activity log of every change.",
        points: [
            "Customer replies and internal notes in one conversation",
            "Multiple assignees and per-ticket time tracking",
            "Merge duplicate requests or forward to another team",
            "Activity log records every status, reply, and assignment",
        ],
    },
    {
        n: "04",
        icon: "link",
        bg: "bg-green",
        accent: "green",
        t: "The customer stays in the loop",
        d: "Instant confirmation, public tracking link, and status emails so they always know where things stand.",
        image: LANDING_IMAGES.liveTracker,
        imageAlt: "Public ticket tracking page where customers follow progress and reply",
        detail:
            "Customers don't have to chase you for updates. They get a public tracking link, no login required, where they can see status, read replies, and respond. Status-change emails and custom confirmation copy per inbox keep them informed without extra work from your team.",
        points: [
            "Public tracking page shows live ticket status",
            "Customers can reply directly from the tracker",
            "Custom confirmation body per incoming inbox",
            "Automatic emails when status changes",
        ],
    },
];

/** Full email-to-resolution walkthrough (How it works page) */
export const WORKFLOW_HIW_STEPS = [
    {
        n: "01",
        icon: "mail",
        bg: "bg-cyan",
        accent: "cyan",
        t: "Create your support inbox",
        d: "Add an incoming email address in Claritick and tell customers to send requests there.",
        image: LANDING_IMAGES.incomingEmails,
        imageAlt: "Incoming emails settings where you add a support address",
        detail:
            "In Incoming Emails, add the address you want customers to use, for example yourcompany@claritick.com, and map it to a department. That address is where every email-based request should land. Widget, in-app, and API intake follow the same ticket path too.",
        points: [
            "Add a support address under Incoming Emails",
            "Map each inbox to the right department",
            "Share the address on your site, signature, or help page",
            "No manual ticket entry, email is the intake channel",
        ],
    },
    {
        n: "02",
        icon: "inbox",
        bg: "bg-cyan",
        accent: "cyan",
        t: "They email and a ticket appears",
        d: "The moment a customer sends to your support address, Claritick creates a ticket automatically.",
        image: LANDING_IMAGES.ticketsQueue,
        imageAlt: "Support tickets list in Claritick showing a new ticket created from inbound email",
        detail:
            "When a message hits your mapped inbox, Claritick turns it into a ticket on the board, no copy-paste, no triage spreadsheet. Follow-up emails thread to the same ticket using the ticket reference in the subject, so the conversation stays in one place.",
        points: [
            "Inbound email becomes a ticket instantly",
            "Replies thread via [Ticket #abc123] in the subject",
            "Smart loop protection keeps threads clean",
            "Widget and API submissions land the same way",
        ],
    },
    {
        n: "03",
        icon: "link",
        bg: "bg-green",
        accent: "green",
        t: "Customer gets confirmation + tracker",
        d: "They receive a “we got your request” email with a public tracking link, no login required.",
        image: LANDING_IMAGES.receiveRequest,
        imageAlt: "Confirmation email with we have received your request and tracking link",
        detail:
            "Right after the ticket is created, the customer gets a confirmation email. It includes their ticket reference and a live tracking link so they can follow progress without chasing you. Pick from five confirmation templates per inbox, each with its own tone and personality.",
        points: [
            "Instant confirmation on every intake",
            "Public tracking link in the email, no account needed",
            "Five confirmation templates to match your brand voice",
            "Custom body copy per incoming inbox",
        ],
    },
    {
        n: "04",
        icon: "bell",
        bg: "bg-gold",
        accent: "gold",
        t: "Your team gets the alert",
        d: "Support receives an email that a new ticket was created, so nothing waits in silence.",
        image: LANDING_IMAGES.newTicketEmail,
        imageAlt: "New ticket notification email sent to the support team",
        detail:
            "While the customer sees their confirmation, your team gets a separate notification that a new ticket is ready. Agents can jump straight into Claritick from the alert, and the ticket is already on the board mapped to the right department.",
        points: [
            "New-ticket email to configured support recipients",
            "Ticket is on the board before anyone opens the app",
            "Department routing applied from inbox mapping",
            "In-app alerts for assignments and replies too",
        ],
    },
    {
        n: "05",
        icon: "users",
        bg: "bg-blue",
        accent: "blue",
        t: "Routed to the right team",
        d: "Department rules, inbox mapping, and auto-assign put each ticket with the right agent.",
        image: LANDING_IMAGES.editDepartment,
        imageAlt: "Department settings mapping incoming email inboxes to support teams",
        detail:
            "Incoming tickets are sorted before anyone opens them. Agents filter the queue by status, priority, or assignee, so the right person picks up the right request without a morning triage ritual.",
        points: [
            "Each inbox maps to a department automatically",
            "Embed submissions auto-assign to the configured team",
            "Filter by department, priority, status, or assignee",
            "Unassigned tickets stay visible so nothing gets missed",
        ],
    },
    {
        n: "06",
        icon: "chat",
        bg: "bg-violet",
        accent: "violet",
        t: "Support works in Claritick",
        d: "Agents reply, add internal notes, track time, and merge duplicates in one thread.",
        image: LANDING_IMAGES.replyEmbedded,
        imageAlt: "Agent composing a reply inside a ticket detail view",
        detail:
            "Your team communicates from inside Claritick. Customer-facing replies go out from the ticket; internal notes stay visible only to agents. Every status change, assignment, and reply is logged in the activity timeline.",
        points: [
            "Customer replies and internal notes in one conversation",
            "Multiple assignees and per-ticket time tracking",
            "Merge duplicates or forward to another team",
            "Outbound replies sent from the department inbox",
        ],
    },
    {
        n: "07",
        icon: "link",
        bg: "bg-green",
        accent: "green",
        t: "Customer stays on the live tracker",
        d: "They follow progress and reply from the tracking page. You work in Claritick, they stay in the loop.",
        image: LANDING_IMAGES.liveTracker,
        imageAlt: "Public ticket tracking page where customers follow progress and reply",
        detail:
            "The customer does not need Claritick. They use the public tracker from their confirmation email. They see live status, read your replies, and respond from the browser. Status-change emails keep them updated when something moves.",
        points: [
            "Public tracking page shows live ticket status",
            "Customers reply directly from the tracker",
            "Automatic emails when status changes",
            "Two channels, one thread, email, tracker, and Claritick stay synced",
        ],
    },
];

export const WORKFLOW_CLARITASK_STEPS = [
    {
        n: "08",
        icon: "link",
        bg: "bg-violet",
        accent: "violet",
        t: "Connect Claritask",
        d: "Link your Claritask workspace in settings so support and delivery share one connected workflow.",
        image: LANDING_IMAGES.claritaskIntegration,
        imageAlt: "Claritask integration enabled in Claritick app settings",
        detail:
            "One-time setup in App Settings. Link your Claritask workspace so agents can push work to delivery when a ticket needs it. Support keeps the customer thread; delivery executes in Claritask.",
        points: [
            "Enable Claritask from Claritick Settings → Apps",
            "Authenticate and link your Claritask workspace",
            "Configure attachment sync per linked task",
            "Included on Standard plans and above",
        ],
    },
    {
        n: "09",
        icon: "chart",
        bg: "bg-gold",
        accent: "gold",
        t: "Hand off to delivery",
        d: "From a ticket, create or link a Claritask task when delivery needs to act.",
        image: LANDING_IMAGES.taskCreation,
        imageAlt: "Create task panel linked from a support ticket into Claritask",
        detail:
            "When a ticket needs engineering or operations, agents spawn a Claritask task without leaving Claritick. Comments and status updates flow between both tools so nothing gets lost.",
        points: [
            "Create or link a Claritask task from any ticket",
            "Ticket attachments can sync to the task",
            "Claritask activity visible as internal notes",
            "Close the ticket when the customer is taken care of",
        ],
    },
];

/** Home workflow teaser & how-it-works core walkthrough */
export const WORKFLOW_STEPS = WORKFLOW_CORE_STEPS;

/** Legacy alias, core ticket path only */
export const STEPS = WORKFLOW_CORE_STEPS;

/* ---------- How it works, channel setup guides ---------- */
export const CHANNEL_SETUP = [
    {
        id: "email",
        label: "Email",
        steps: [
            "Go to Incoming Emails and click + Add Email",
            "Enter your support address and map it to a department",
            "Send a test email and it becomes a ticket automatically",
            "Optional: customize confirmation body and enable public tracking",
        ],
    },
    {
        id: "widget",
        label: "Widget",
        steps: [
            "Open Embed Ticket Forms and create a new form",
            "Choose inline or floating display, set branding and fields",
            "Add your website to Trusted Domains",
            "Copy the embed snippet and paste it on your site",
        ],
    },
    {
        id: "inapp",
        label: "In-app",
        steps: [
            "Invite agents and customers under Users",
            "Assign users to the right Departments",
            "Agents create tickets from + Add Ticket in the queue",
            "Customers submit in their allowed departments only",
        ],
    },
    {
        id: "api",
        label: "API",
        steps: [
            "Open Developer Options and generate an API key",
            "Use /api/dev/v1/ endpoints for tickets, users, and more",
            "Refer to the built-in API docs in the app",
            "Automate intake from any system you already run",
        ],
    },
];

/* ---------- Ticket statuses ---------- */
export const TICKET_STATUSES = [
    { id: "open", label: "Open", color: "#0087e2" },
    { id: "active", label: "Active", color: "#059669" },
    { id: "pending", label: "Pending", color: "#d97706" },
    { id: "clarification", label: "Need Clarification", color: "#7c3aed" },
    { id: "confirmation", label: "Need Confirmation", color: "#db2777" },
    { id: "closed", label: "Closed", color: "#6b7280" },
    { id: "rejected", label: "Rejected", color: "#dc2626" },
    { id: "spam", label: "Spam", color: "#9ca3af" },
];

/* ---------- Feature catalog (deep dive) ---------- */
export const FEATURE_CATEGORIES = [
    {
        id: "creation",
        label: "Creation",
        slides: [
            {
                id: "intake-screenshot",
                image: LANDING_IMAGES.ticketScreenshot,
                imageAlt: "New support request captured in Claritick",
                t: "Every channel becomes a ticket",
                d: "Email, widget, in-app, or API: the request lands in Claritick instantly.",
            },
            {
                id: "received-request",
                image: LANDING_IMAGES.receiveRequest,
                imageAlt: "Confirmation email: we have received your request",
                t: "We have received your request",
                d: "Customers get an instant confirmation with their ticket reference and tracking link.",
            },
        ],
        features: [
            {
                id: "email-to-ticket",
                icon: "mail",
                visual: "inbox",
                tag: "Creation",
                title: "Email-to-ticket",
                description:
                    "Every support email becomes a structured ticket automatically, with the full conversation kept in one thread.",
                points: [
                    "Every support email becomes a ticket",
                    "Replies stay in the same thread",
                    "No duplicate or messy email loops",
                ],
            },
            {
                id: "confirmations",
                icon: "check",
                visual: "confirm",
                tag: "Creation",
                title: "Instant confirmation",
                description:
                    "Customers get an immediate confirmation with their ticket reference and a link to track progress.",
                points: [
                    "Customers get a confirmation with a tracking link",
                    "Choose a confirmation style for each inbox",
                    "Widget and API requests work the same way",
                ],
            },
            {
                id: "email-flows",
                icon: "inbox",
                visual: "email",
                tag: "Creation",
                title: "Special email flows",
                description:
                    "Handle clarifications, CCs, and department-specific reply addresses without leaving your inbox workflow.",
                points: [
                    "Ask customers to clarify or confirm",
                    "Copy extra people on notifications",
                    "Replies send from your department address",
                ],
            },
        ],
    },
    {
        id: "tickets",
        label: "Tickets",
        slides: [
            {
                id: "ticket-reply",
                image: LANDING_IMAGES.replyEmbedded,
                imageAlt: "Agent composing a reply inside a ticket",
                t: "Reply and collaborate",
                d: "Customer replies, internal notes, timers, and merge in one threaded view.",
            },
            {
                id: "ticket-queue",
                image: LANDING_IMAGES.ticketsQueue,
                imageAlt: "Support tickets listed in the Claritick queue",
                t: "Your live queue",
                d: "Filter by status, department, assignee, or priority so nothing gets lost.",
            },
            {
                id: "public-tracker",
                image: LANDING_IMAGES.liveTracker,
                imageAlt: "Public ticket tracking page for customers",
                t: "Public tracking",
                d: "Customers follow progress and reply without logging in.",
            },
        ],
        features: [
            {
                id: "ticket-board",
                icon: "activity",
                visual: "tickets",
                tag: "Tickets",
                title: "Live ticket board",
                description:
                    "See every open ticket at a glance and filter by status, team, priority, or assignee so nothing gets lost.",
                points: [
                    "Filter by status, team, priority, and assignee",
                    "Search tickets and update many at once",
                    "Get alerts when tickets need attention",
                ],
            },
            {
                id: "ticket-details",
                icon: "ticket",
                visual: "thread",
                tag: "Tickets",
                title: "Ticket details",
                description:
                    "Work the full conversation, attachments, internal notes, and time tracking in one threaded view.",
                points: [
                    "Full conversation with attachments",
                    "Assign multiple agents and add internal notes",
                    "Track time spent on each ticket",
                ],
            },
            {
                id: "merge-forward",
                icon: "paperclip",
                visual: "merge",
                tag: "Tickets",
                title: "Merge & forward",
                description:
                    "Clean up duplicate requests, hand tickets off externally, and lock conversations once they are resolved.",
                points: [
                    "Combine duplicate tickets into one",
                    "Forward a ticket to someone outside Claritick",
                    "Lock closed tickets after they are resolved",
                ],
            },
        ],
    },
    {
        id: "departments",
        label: "Departments",
        slides: [
            {
                id: "edit-department",
                image: LANDING_IMAGES.editDepartment,
                imageAlt: "Department settings with team members and inbox mapping",
                t: "Organize your teams",
                d: "Departments with their own users, inboxes, and routing rules.",
            },
            {
                id: "map-inbox",
                image: LANDING_IMAGES.ticketEmail,
                imageAlt: "Incoming email address mapped to a department in Claritick",
                t: "Map your support inbox",
                d: "Connect a support address and every message routes to the right department.",
            },
        ],
        features: [
            {
                id: "departments",
                icon: "users",
                visual: "teams",
                tag: "Departments",
                title: "Department routing",
                description:
                    "Organize teams with their own users, inboxes, and routing rules so every request lands with the right people.",
                points: [
                    "Teams with their own users and inboxes",
                    "Route tickets to the right team automatically",
                    "See ticket and email counts per team",
                ],
            },
            {
                id: "users-roles",
                icon: "shield",
                visual: "roles",
                tag: "Departments",
                title: "Users & roles",
                description:
                    "Give admins, agents, and customers the right level of access and invite teammates in a few clicks.",
                points: [
                    "Admins, agents, and customers with the right access",
                    "Invite teammates by email",
                    "Switch between workspaces from one place",
                ],
            },
            {
                id: "taxonomy",
                icon: "star",
                visual: "tags",
                tag: "Departments",
                title: "Types, priorities & tags",
                description:
                    "Define custom ticket types, priority levels, and tags to match how your team actually works.",
                points: [
                    "Custom ticket types",
                    "Priority levels and tags",
                    "Organize tickets your way",
                ],
            },
        ],
    },
    {
        id: "forms",
        label: "Forms",
        slides: [
            {
                id: "embed-live",
                image: LANDING_IMAGES.embedFormExact,
                imageAlt: "Embedded support form live on a website",
                t: "Live on your website",
                d: "Branded embed form collecting tickets directly from visitors.",
            },
            {
                id: "embed-script",
                image: LANDING_IMAGES.embedFormScript,
                imageAlt: "Embed form script snippet from Claritick settings",
                t: "Create and copy the embed snippet",
                d: "Configure the form in Claritick, then grab the script for your site.",
            },
        ],
        features: [
            {
                id: "embed-forms",
                icon: "widget",
                visual: "form",
                tag: "Forms",
                title: "Embed ticket forms",
                description:
                    "Build branded support forms and embed them on your site or launch them from a chat-style button.",
                points: [
                    "Build a form in a simple step-by-step wizard",
                    "Embed on your site or use a chat-style button",
                    "Match your branding and thank-you page",
                ],
            },
            {
                id: "trusted-domains",
                icon: "map-pin",
                visual: "shield",
                tag: "Forms",
                title: "Trusted domains",
                description:
                    "Accept submissions only from your websites, route new tickets to the right agent, and collect custom fields.",
                points: [
                    "Only accept submissions from your websites",
                    "Auto-assign new form tickets to the right agent",
                    "Add custom fields to your form",
                ],
            },
        ],
    },
    {
        id: "reporting",
        label: "Reporting",
        slides: [
            {
                id: "summary-report",
                image: LANDING_IMAGES.emailTicketsSummary,
                imageAlt: "Ticket summary report with status counts in Claritick",
                t: "Summary inside Claritick",
                d: "Status breakdown and ticket metrics ready to email or export.",
            },
            {
                id: "summary-email",
                image: LANDING_IMAGES.ticketsSummaryEmailView,
                imageAlt: "Scheduled ticket summary email in the inbox",
                t: "Digest in your inbox",
                d: "Scheduled summary emails land where your team already works.",
            },
        ],
        features: [
            {
                id: "dashboard",
                icon: "chart",
                visual: "dashboard",
                tag: "Reporting",
                title: "Custom dashboard",
                description:
                    "Arrange widgets for status counts, charts, and recent activity, filtered by agent, team, and date.",
                points: [
                    "Arrange your dashboard widgets",
                    "Status counts, charts, and recent activity",
                    "Filter by agent, team, and date",
                ],
            },
            {
                id: "reports",
                icon: "eye",
                visual: "reports",
                tag: "Reporting",
                title: "Five report modules",
                description:
                    "Run overview, activity, ticket, team, and time reports, then download or email them anytime.",
                points: [
                    "Overview, activity, tickets, team, and time reports",
                    "Download as CSV or PDF",
                    "Email a summary report anytime",
                ],
            },
            {
                id: "digests",
                icon: "bell",
                visual: "digest",
                tag: "Reporting",
                title: "Scheduled digests",
                description:
                    "Send daily or weekly summary emails with resolution counts, response times, and open ticket totals.",
                points: [
                    "Daily or weekly summary emails",
                    "Resolution counts, response times, and open tickets",
                    "Set schedules per team",
                ],
            },
        ],
    },
    {
        id: "integrations",
        label: "Integrations",
        slides: [
            {
                id: "claritask-connect",
                image: LANDING_IMAGES.claritaskIntegration,
                imageAlt: "Claritask integration enabled in Claritick app settings",
                t: "Connect Claritask",
                d: "One-time setup in App Settings to link your Claritask workspace.",
            },
            {
                id: "claritask-task",
                image: LANDING_IMAGES.taskCreation,
                imageAlt: "Create task panel linked from a support ticket into Claritask",
                t: "Hand off to delivery",
                d: "Spawn a Claritask task from the ticket when delivery needs to act.",
            },
        ],
        features: [
            {
                id: "claritask",
                icon: "link",
                visual: "sync",
                tag: "Integrations",
                title: "Claritask sync",
                description:
                    "Create and link Claritask tasks from a ticket, sync attachments, and read task updates without switching apps.",
                points: [
                    "Create and link Claritask tasks from a ticket",
                    "Sync attachments when you hand off work",
                    "Read task comments and notes on the ticket",
                ],
            },
            {
                id: "developer-api",
                icon: "bolt",
                visual: "api",
                tag: "Integrations",
                title: "Developer API",
                description:
                    "Connect Claritick to your own tools with built-in API docs and bulk data export when you need it.",
                points: [
                    "Connect Claritick to your own tools",
                    "API docs built into the app",
                    "Export your data in bulk",
                ],
            },
            {
                id: "mydev",
                icon: "sparkles",
                visual: "ecosystem",
                tag: "Integrations",
                title: "MyDev ecosystem",
                description:
                    "Sign in with MyDev, manage billing in one place, and store files in your shared media library.",
                points: [
                    "Sign in with your MyDev account",
                    "Manage billing and subscription in one place",
                    "Store files in your media library",
                ],
            },
        ],
    },
];

export const FEATURE_DIVE_ITEMS = FEATURE_CATEGORIES.flatMap((category) =>
    category.features.map((feature) => ({
        ...feature,
        uid: `${category.id}:${feature.id}`,
        tag: category.label.toUpperCase(),
    }))
);

export const FEATURE_CATEGORY_MODAL_STEPS = FEATURE_CATEGORIES.flatMap((category) =>
    (category.slides || []).map((slide) => ({
        id: slide.id,
        t: slide.t,
        d: slide.d,
        image: slide.image,
        imageAlt: slide.imageAlt || "",
    }))
);

/* ---------- Analytics section ---------- */
export const ANALYTICS_BULLETS = [
    "Status counts updated as tickets move: open, active, overdue, closed, and more",
    "Charts for tickets created, resolved trends, priority mix, and department volume",
    "Filter by agent, department, and date range on every widget",
    "Email summary button sends leadership a snapshot with attachments",
    "Top agents leaderboard and recent activity feed",
];

/* ---------- Customization cards ---------- */
export const CUSTOMIZATION_CARDS = [
    {
        icon: "chart",
        bg: "bg-blue",
        t: "Dashboard your way",
        d: "Drag widgets, pick charts, and save your layout. Every agent sees what matters to them.",
    },
    {
        icon: "widget",
        bg: "bg-cyan",
        t: "Forms your way",
        d: "Colors, position, custom fields, and success pages with no developer needed.",
    },
    {
        icon: "bell",
        bg: "bg-violet",
        t: "Alerts your way",
        d: "Department digests, notification preferences, and custom confirmation emails.",
    },
];

/* ---------- Social proof ---------- */
export const SOCIAL_PROOF = {
    quote: "Claritick turned our scattered email threads into a support queue we can actually manage. We went live in an afternoon and haven't looked back.",
    name: "Support Team Lead",
    role: "Software Dev · Kore.co",
    stats: [
        { value: "233+", label: "Tickets managed" },
        { value: "3", label: "Departments routed" },
        { value: "< 1 day", label: "Average time to go live" },
    ],
};

/* ---------- Claritask landing page ---------- */
export const CLARITASK_PAGE = {
    hero: {
        eyebrow: "Claritick + Claritask",
        title: "Tickets capture the request. Tasks get it done.",
        summary:
            "Customers reach you through Claritick. When a ticket needs engineering, operations, or delivery work, hand it off to Claritask without losing context.",
        callout:
            "Claritick handles customer intake. Claritask is where your team executes the work. Connect both and stop losing context between support and delivery.",
    },
    premise: {
        title: "Intake and execution, finally connected",
        lead: "When support cannot solve it alone, hand the work to Claritask without copy-pasting into another system or losing the customer thread.",
        columns: [
            {
                label: "Claritick",
                role: "Support intake",
                points: [
                    "Email, widget, and in-app tickets",
                    "Customer replies and public tracking",
                    "Assign, route, and close from one queue",
                ],
            },
            {
                label: "Claritask",
                role: "Delivery execution",
                points: [
                    "Tasks for engineering and operations",
                    "Projects, assignees, and due dates",
                    "Work completed where delivery lives",
                ],
            },
        ],
    },
    flowSteps: [
        {
            id: "connect",
            visual: "connect",
            title: "Connect once in Apps settings",
            detail:
                "An admin links your Claritick workspace to Claritask in a few clicks. No developers, no custom code. Your support and delivery teams share one connected workflow.",
            points: [
                "One-time setup under Settings → Apps",
                "Included on Standard plans and above",
                "Live in minutes with no custom code",
            ],
        },
        {
            id: "handoff",
            visual: "handoff",
            title: "Create or link a task from the ticket",
            detail:
                "When a ticket needs delivery action, spawn a Claritask task or link an existing one. The ticket stays in Claritick for customer communication.",
            points: [
                "Create task with project, assignees, and due date",
                "Link an existing task by search",
                "Sync files from ticket attachments",
            ],
        },
        {
            id: "sync",
            visual: "sync",
            title: "Both teams stay in the loop",
            detail:
                "Read Claritask comments on the ticket, get internal notes when delivery updates, and notify linked tasks when ticket status changes.",
            points: [
                "Claritask Comments tab on every linked ticket",
                "Ticket status changes post to linked tasks",
                "Claritask activity returns as internal notes",
            ],
        },
        {
            id: "resolve",
            visual: "resolve",
            title: "Close the loop with the customer",
            detail:
                "Delivery completes work in Claritask. Support closes the ticket in Claritick when the customer is taken care of. One thread for the customer, two tools for the team.",
            points: [
                "Customer stays on Claritick tracking and email",
                "Delivery works in Claritask boards",
                "No duplicate intake or lost context",
            ],
        },
    ],
    integrationCategories: [
        {
            id: "connect",
            label: "Connect & handoff",
            visual: "connect",
            features: [
                {
                    id: "workspace-link",
                    icon: "link",
                    tag: "Setup",
                    title: "Link your workspace",
                    copy: "An admin connects Claritick to Claritask under Settings → Apps: pick your Claritask company, authenticate once, and toggle the integration on or off for the whole workspace.",
                    points: [
                        "Connect under Settings → Apps",
                        "Authenticate with your Claritask company once",
                        "Toggle the integration on or off for the workspace",
                    ],
                },
                {
                    id: "create-task",
                    icon: "bolt",
                    tag: "Handoff",
                    title: "Create tasks from tickets",
                    copy: "From ticket detail, open the Linked Claritask panel and create a task with project, assignees, and due date. The ticket stays open in Claritick for every customer reply.",
                    points: [
                        "Create tasks from the Linked Claritask panel",
                        "Set project, assignees, and due date at handoff",
                        "Keep the ticket open for every customer reply",
                    ],
                },
                {
                    id: "link-task",
                    icon: "search",
                    tag: "Handoff",
                    title: "Link, search, and unlink",
                    copy: "Search Claritask for an existing task and link it to the ticket, link more than one when needed, or unlink and remove connections without losing the ticket thread.",
                    points: [
                        "Search Claritask and link existing tasks",
                        "Link more than one task when needed",
                        "Unlink connections without losing the ticket thread",
                    ],
                },
            ],
        },
        {
            id: "activity",
            label: "Activity & notes",
            visual: "activity",
            features: [
                {
                    id: "comments-tab",
                    icon: "chat",
                    tag: "Comments",
                    title: "Claritask Comments tab",
                    copy: "Agents open the Claritask Comments tab on a linked ticket to read live task comments, authors, and attachment metadata pulled from Claritask without leaving Claritick.",
                    points: [
                        "Read live Claritask comments on the ticket",
                        "See authors and attachment metadata",
                        "Stay in Claritick — no tab switching",
                    ],
                },
                {
                    id: "status-notify",
                    icon: "bell",
                    tag: "Updates",
                    title: "Ticket status → task updates",
                    copy: "When an agent changes ticket status in Claritick, linked Claritask tasks receive an activity update so delivery knows support moved the conversation forward.",
                    points: [
                        "Ticket status changes notify linked tasks",
                        "Delivery sees when support moves forward",
                        "Activity stays tied to the same ticket",
                    ],
                },
                {
                    id: "internal-notes",
                    icon: "pencil",
                    tag: "Notes",
                    title: "Shared internal notes",
                    copy: "Activity from Claritask can post back as an internal note on the ticket. Claritask can also pull Claritick internal notes through the integration API so both sides share context.",
                    points: [
                        "Claritask activity posts as internal notes",
                        "Pull Claritick notes through the API",
                        "Both teams share context behind the scenes",
                    ],
                },
            ],
        },
        {
            id: "files",
            label: "Attachments",
            visual: "files",
            features: [
                {
                    id: "sync-at-handoff",
                    icon: "paperclip",
                    tag: "Files",
                    title: "Sync files at handoff",
                    copy: "When creating or linking a task, turn on file sync to push existing ticket attachments into Claritask so delivery starts with the same files support already received.",
                    points: [
                        "Push ticket attachments at handoff",
                        "Delivery starts with the same files",
                        "Enable file sync when you create or link",
                    ],
                },
                {
                    id: "reply-attachments",
                    icon: "mail",
                    tag: "Files",
                    title: "New replies push files",
                    copy: "After file sync is enabled on a link, agent replies that include attachments can forward those files to the linked Claritask tasks automatically.",
                    points: [
                        "New agent reply attachments forward automatically",
                        "Works once file sync is enabled on the link",
                        "Linked tasks stay up to date with support files",
                    ],
                },
                {
                    id: "sync-toggle",
                    icon: "widget",
                    tag: "Files",
                    title: "Per-link file sync",
                    copy: "Each ticket–task link stores its own sync preference. Toggle file sync when you create or link a task, or adjust it later through the integration so only the right files cross over.",
                    points: [
                        "Each ticket–task link has its own sync setting",
                        "Toggle sync when you create or link a task",
                        "Adjust preferences later without breaking the link",
                    ],
                },
            ],
        },
        {
            id: "visibility",
            label: "Who sees what",
            visual: "visibility",
            features: [
                {
                    id: "task-cards",
                    icon: "ticket",
                    tag: "Ticket view",
                    title: "Linked task cards",
                    copy: "Linked Claritask tasks appear on the ticket with assignees, due dates, and deep links back to Claritask so support knows who owns delivery and what is due.",
                    points: [
                        "Linked tasks show on the ticket detail view",
                        "See assignees, due dates, and deep links",
                        "Support knows who owns delivery and when",
                    ],
                },
                {
                    id: "agent-visibility",
                    icon: "users",
                    tag: "Agents",
                    title: "Support reads activity",
                    copy: "Agents follow Claritask Comments and internal notes on the ticket while they reply to the customer in the main conversation thread. Customer-facing email stays standard Claritick delivery.",
                    points: [
                        "Follow Claritask Comments on the ticket",
                        "Read internal notes while replying to customers",
                        "Customer email stays standard Claritick delivery",
                    ],
                },
                {
                    id: "customer-thread",
                    icon: "eye",
                    tag: "Customers",
                    title: "Customers stay on Claritick",
                    copy: "Customers never need a Claritask login. They email your support address, use the public tracker, and get normal ticket notifications while your delivery team works behind the scenes.",
                    points: [
                        "No Claritask login required for customers",
                        "Email replies and public tracker only",
                        "Delivery works behind the scenes in Claritask",
                    ],
                },
            ],
        },
    ],
};

export const CLARITASK_INTEGRATION_ITEMS = CLARITASK_PAGE.integrationCategories.flatMap((category) =>
    category.features.map((feature) => ({
        ...feature,
        uid: `${category.id}:${feature.id}`,
        tag: feature.tag.toUpperCase(),
        description: feature.copy,
        visual: feature.id,
    }))
);

/* ---------- FAQ (home + contact) ---------- */
export const HOME_FAQ = [
    {
        q: "How do I get started with Claritick?",
        a: "Create a free account and you'll be up and running in minutes. Connect a support inbox, add the widget to your site, and invite your team. No credit card required to begin.",
    },
    {
        q: "Where do tickets come from?",
        a: "Email, embeddable website forms, in-app creation, and the Developer API all land on the same ticket board with threading, assignments, and status tracking.",
    },
    {
        q: "Can customers track their requests?",
        a: "Yes. Every ticket can include a public tracking link so customers view status and reply without logging in.",
    },
    {
        q: "Does Claritick integrate with Claritask?",
        a: "Yes. Connect your workspace in App Settings, then create or link Claritask tasks from tickets. Agents read Claritask comments on the ticket, ticket status changes notify linked tasks, delivery updates can appear as internal notes, and you can sync attachments when you hand off work.",
    },
    {
        q: "Do you offer enterprise or custom setups?",
        a: "Yes. For advanced needs like custom onboarding, SLAs, or multi-brand workspaces, email us at info@claritick.com and we'll put together a plan that fits.",
    },
];

export const CONTACT_FAQ = HOME_FAQ;

export const PLANS = [
    {
        name: "Free", price: "$0", per: "/mo", desc: "For getting started with email support.",
        cta: "Start free", url: REGISTER_URL, featured: false,
        feats: ["1 support inbox", "Email-to-ticket", "Public ticket tracking", "Up to 2 agents"],
    },
    {
        name: "Standard", price: "$10", per: "/mo", desc: "For growing teams that need more channels.",
        cta: "Start free trial", url: "https://app.ipaymer.com/package/a12d02f7-5f68-4565-a8ca-a3da441d6a94", featured: true,
        feats: ["Everything in Free", "Website support forms", "Claritask integration", "Roles & permissions"],
    },
    {
        name: "Premium", price: "$20", per: "/mo", desc: "For busy teams that want it all.",
        cta: "Start free trial", url: "https://app.ipaymer.com/package/a12d03e1-5854-4c1c-b962-eca0d8b5935d", featured: false,
        feats: ["Everything in Standard", "Scheduled summaries", "Unlimited workspaces", "Priority support"],
    },
    {
        name: "Enterprise", price: "Let's talk", per: "", desc: "For organizations with advanced needs.",
        cta: "Talk to sales", url: "https://kore.co/contact", featured: false,
        feats: ["Everything in Premium", "Custom onboarding", "Dedicated support", "Custom SLAs"],
    },
];
